import React, { useState } from "react";
import { Shield, Clock, CheckCircle, User, FileCheck } from "lucide-react";
import HeroSection from "@/components/HeroSection";
import ButtonCTA from "@/components/ButtonCTA";
import SEO from "@/components/SEO";
import HeadingHighlight from "@/components/HeadingHighlight";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import { graphql, PageProps } from "gatsby";
import RegionsList from "../components/regions/RegionsList";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProfessionsList from "@/components/ProfessionsList";
import { CRJMC_PRICE } from "@/constants";

interface IndexData {
  site: {
    siteMetadata: {
      siteUrl: string;
    };
  };
}

export const query = graphql`
  query IndexPage {
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;

declare global {
  namespace JSX {
    interface IntrinsicElements {
      picture: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

interface StepCardProps {
  number: string | number;
  title: string;
  duration: string;
}

interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  description: React.ReactNode;
}

const StepCard = ({ number, title, duration }: StepCardProps) => (
  <div className="bg-white p-6 rounded-lg border border-emerald-200 relative">
    <div className="hidden md:flex absolute -top-4 -left-4 bg-emerald-700 text-white w-8 h-8 rounded-full items-center justify-center font-bold">
      {number}
    </div>
    <div className="flex md:hidden bg-emerald-700 text-white w-8 h-8 rounded-full items-center justify-center font-bold absolute top-2 left-2">
      {number}
    </div>
    <div className="mb-3 ml-12 md:ml-0">
      <h3 className="font-bold">{title}</h3>
    </div>
    <p className="text-gray-600">{duration}</p>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description }: FeatureCardProps) => (
  <div className="bg-white p-6 rounded-lg border border-gray-200">
    <Icon className="text-emerald-700 mb-4" size={32} />
    <h3 className="font-bold mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

const ScrollPopup = () => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [hasShown, setHasShown] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      if (typeof window !== "undefined" && !hasShown) {
        const scrollThreshold = document.documentElement.scrollHeight * 0.5;
        if (window.scrollY > scrollThreshold) {
          setIsVisible(true);
          setHasShown(true);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasShown]);

  const handleClose = () => {
    setIsVisible(false);
  };

  return (
    <div
      className={`fixed bottom-4 right-4 bg-white p-6 rounded-lg shadow-lg border-2 border-emerald-700 transition-opacity duration-300 ${
        isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ maxWidth: "400px" }}
    >
      <button
        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
        onClick={handleClose}
      >
        ✕
      </button>
      <h3 className="text-xl font-bold text-emerald-700 mb-3">
        Ready to Get Started?
      </h3>
      <p className="text-gray-600 mb-4">
        Complete your CRJMC application quickly and securely. Get your results
        fast!
      </p>
      <button className="w-full bg-emerald-700 text-white px-6 py-3 rounded-lg font-bold hover:bg-emerald-800 transition-colors">
        Apply Now - CAD${CRJMC_PRICE}
      </button>
    </div>
  );
};

export const Head = () => [
  <SEO
    key="seo"
    title="Get Your Criminal Record Check (CRJMC) Online in 15 Minutes"
    description="Apply for your Criminal Record & Judicial Matters Check (CRJMC) online in just 15 minutes. Fast, reliable, and officially accepted for Security Guard License in Ontario."
    image="/images/security-guard/security-guard-with-background-check-cleared-small.webp"
    article={false}
    pathname="/"
    ogType="website"
    ogTitle="Get Your CRJMC Online in 15 Minutes | CRJMC.net"
    ogDescription="Fast, reliable Criminal Record Check for Security Guard License in Ontario. Apply online and get results in 15 minutes."
    ogImage="/images/security-guard/security-guard-with-background-check-cleared-small.webp"
    twitterCard="summary_large_image"
    twitterTitle="Get Your CRJMC Online in 15 Minutes | CRJMC.net"
    twitterDescription="Fast, reliable Criminal Record Check for Security Guard License in Ontario. Apply online and get results in 15 minutes."
    twitterImage="/images/security-guard/security-guard-with-background-check-cleared-small.webp"
  />,
  <script
    key="jsonld"
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": "https://crjmc.net/#organization",
            name: "CRJMC.net",
            url: "https://crjmc.net",
            description:
              "Providing Criminal Record & Judicial Matters Check (CRJMC) services online",
            contactPoint: {
              "@type": "ContactPoint",
              email: "support@crjmc.net",
              contactType: "customer service",
            },
          },
          {
            "@type": "WebSite",
            "@id": "https://crjmc.net/#website",
            url: "https://crjmc.net",
            name: "CRJMC - Criminal Record & Judicial Matters Check",
            description:
              "Get your Criminal Record & Judicial Matters Check (CRJMC) online in just 15 minutes. Fast, reliable, and officially accepted.",
            publisher: {
              "@id": "https://crjmc.net/#organization",
            },
          },
          {
            "@type": "Product",
            "@id": "https://crjmc.net/#service",
            name: "Criminal Record & Judicial Matters Check",
            description:
              "Online Criminal Record & Judicial Matters Check service, delivered within 15 minutes",
            provider: {
              "@id": "https://crjmc.net/#organization",
            },
            areaServed: {
              "@type": "State",
              name: "Ontario",
              addressCountry: "CA",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              reviewCount: "257",
              bestRating: "5",
              worstRating: "3",
            },
            review: [
              {
                "@type": "Review",
                reviewRating: {
                  "@type": "Rating",
                  ratingValue: "5",
                  bestRating: "5",
                },
                author: {
                  "@type": "Person",
                  name: "David Rodriguez",
                },
                datePublished: "2024-11",
                reviewBody:
                  "As someone transitioning into private investigation work, I needed my background check done quickly. This service delivered exactly that - fast, reliable results that were accepted by the licensing board. The online process saved me so much time.",
                description:
                  "Got CRJMC in November 2024 for Private Investigation License",
              },
              {
                "@type": "Review",
                reviewRating: {
                  "@type": "Rating",
                  ratingValue: "5",
                  bestRating: "5",
                },
                author: {
                  "@type": "Person",
                  name: "Emily Patel",
                },
                datePublished: "2024-03",
                reviewBody:
                  "I was worried about getting my background check done in time for my new mall security position. This service made it so simple - the whole process took minutes and I had my results the same day. Highly recommend for anyone in the security industry.",
                description:
                  "Got CRJMC in March 2024 for a Security Guard License renewal",
              },
              {
                "@type": "Review",
                reviewRating: {
                  "@type": "Rating",
                  ratingValue: "5",
                  bestRating: "5",
                },
                author: {
                  "@type": "Person",
                  name: "Michael Chen",
                },
                datePublished: "2024-12",
                reviewBody:
                  "The process was incredibly smooth and quick. I received my results much faster than expected. Instead of spending weeks going through the police station, I had everything I needed in minutes. CRJMC.net is a great service for anyone needing their background check done in Ontario quickly. The online record check system is very convenient.",
                description:
                  "Got CRJMC in December 2024 for Security Guard Application",
              },
              {
                "@type": "Review",
                reviewRating: {
                  "@type": "Rating",
                  ratingValue: "5",
                  bestRating: "5",
                },
                author: {
                  "@type": "Person",
                  name: "Danzel Williams",
                },
                datePublished: "2025-01",
                reviewBody:
                  "I was amazed at how straightforward the whole process was. Completed everything from my laptop during my lunch break. The service is transparent with pricing and delivered exactly what was promised. Would definitely recommend to other security professionals.",
                description:
                  "Got CRJMC in January 2025 for a new Security Guard License",
              },
            ],
            brand: {
              "@type": "Brand",
              name: "CRJMC.net",
            },
            category: "Background Check",
            productionDate: new Date().toISOString().split("T")[0],
            purchaseDate: new Date().toISOString().split("T")[0],
            releaseDate: new Date().toISOString().split("T")[0],
            award: "Authorized Third Party Provider",
            image: {
              "@type": "ImageObject",
              url: "https://crjmc.net/images/cleared-background-check-512.png",
            },
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://crjmc.net",
              },
            ],
          },
          {
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How much does a CRJMC cost?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: `The CRJMC costs CAD $${CRJMC_PRICE} at CRJMC.net. This includes fast 15-minute processing, secure online delivery, and no in-person visits. We also offer a 20% discount on renewals.`,
                },
              },
              {
                "@type": "Question",
                name: "What's included in a CRJMC?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "A CRJMC includes criminal convictions, outstanding charges and arrest warrants, court orders (such as restraining orders or probation orders), and absolute and conditional discharges.",
                },
              },
              {
                "@type": "Question",
                name: "How long does it take to get my CRJMC?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "At CRJMC.net, you'll receive your police record check within 15 minutes. Our system is fully automated and designed to provide fast, secure results, delivered directly to your inbox. There's no need for an in-person appointment.",
                },
              },
              {
                "@type": "Question",
                name: "Do I need to visit a police station?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No! Everything is done online from start to finish. Unlike traditional police services in Ontario, there's no need to visit a station or book an appointment. Simply complete your request online and get results in 15 minutes.",
                },
              },
              {
                "@type": "Question",
                name: "Who needs a CRJMC?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "A CRJMC is commonly required for security guards, private investigators, mall security, airport security, and other roles in positions of trust.",
                },
              },
              {
                "@type": "Question",
                name: "Is the process secure?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes, our process is secure and fully compliant with Canadian privacy laws. We use advanced ID verification technology to verify your identity online, ensuring your information remains safe and confidential.",
                },
              },
            ],
          },
        ],
      }),
    }}
  />,
];

const CRJMCLanding: React.FC<PageProps<IndexData>> = ({ data }) => {
  const { siteUrl } = data.site.siteMetadata;
  const [isRegionsExpanded, setIsRegionsExpanded] = useState(false);

  return (
    <div>
      <Header />
      <main>
        {/* Hero Section */}
        <HeroSection />

        {/* CRJMC Validity Section - NEW */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <HeadingHighlight>What is a CRJMC?</HeadingHighlight>
              <p className="text-gray-600 mt-6 text-lg max-w-3xl mx-auto">
                A Criminal Record and Judicial Matters Check (CRJMC) is a
                comprehensive background verification required for professional
                licensing in Ontario.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-lg border border-emerald-100">
                <h3 className="text-xl font-bold mb-4 text-emerald-700">
                  What's Included in Your CRJMC?
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 mr-2 flex-shrink-0" />
                    <span>
                      <strong>Criminal convictions</strong> and{" "}
                      <strong>findings of guilt</strong> under the{" "}
                      <strong>Criminal Code</strong>
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 mr-2 flex-shrink-0" />
                    <span>
                      <strong>Outstanding warrants</strong> and{" "}
                      <strong>charges</strong> across <strong>Canada</strong>
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 mr-2 flex-shrink-0" />
                    <span>
                      <strong>Judicial orders</strong>, including{" "}
                      <strong>peace bonds</strong> and{" "}
                      <strong>probation orders</strong>
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 mr-2 flex-shrink-0" />
                    <span>
                      Findings under the{" "}
                      <strong>Youth Criminal Justice Act</strong> within access
                      periods
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-lg border border-emerald-100">
                <h3 className="text-xl font-bold mb-4 text-emerald-700">
                  Validity and Acceptance
                </h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 mr-2 flex-shrink-0" />
                    <span>
                      Universally accepted format across all Ontario regions
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 mr-2 flex-shrink-0" />
                    <span>
                      Valid for security guard, private investigator and tow
                      operator licensing
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 mr-2 flex-shrink-0" />
                    <span>
                      Digital delivery with secure verification system
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-emerald-600 mt-1 mr-2 flex-shrink-0" />
                    <span>
                      Compliant with Ministry of the Solicitor General
                      requirements
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Eligibility Section - NEW */}
        <section className="py-16 bg-gray-100">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <HeadingHighlight>CRJMC Check Eligibility</HeadingHighlight>
              <p className="text-gray-600 mt-6 text-lg max-w-3xl mx-auto">
                Before applying for your CRJMC, ensure you meet the following
                eligibility criteria:
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md border border-emerald-100">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                  <User className="w-6 h-6 text-emerald-700" />
                </div>
                <h3 className="text-lg font-bold mb-2">Age Requirement</h3>
                <p className="text-gray-600">
                  Must be 18 years or older to apply for a CRJMC check
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border border-emerald-100">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                  <FileCheck className="w-6 h-6 text-emerald-700" />
                </div>
                <h3 className="text-lg font-bold mb-2">Valid ID</h3>
                <p className="text-gray-600">
                  Government-issued photo ID (Driver's License, Passport,
                  Ontario Photo Card or other)
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md border border-emerald-100">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-emerald-700" />
                </div>
                <h3 className="text-lg font-bold mb-2">Ontario Residency</h3>
                <p className="text-gray-600">
                  Current or recent Ontario address required for verification
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Do you need a CRJMC Section */}
        <section className="py-16 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <HeadingHighlight>Who needs a CRJMC in Ontario?</HeadingHighlight>
            <div className="mt-12">
              <ProfessionsList />
            </div>
          </div>
        </section>

        {/* Mid-page CTA Section - NEW */}
        <section className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white py-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">
                  Do you want to get your CRJMC today?
                </h2>
                <div className="space-y-3 mb-6">
                  <p className="text-lg">
                    Get your CRJMC check today and take the first step towards
                    your security guard license.
                  </p>
                  <p className="text-lg font-semibold">
                    CAD${CRJMC_PRICE} - Results in 15 Minutes
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                  <div className="w-full sm:w-auto">
                    <ButtonCTA
                      buttonText="Apply for CRJMC now"
                      showSubheader={false}
                    />
                  </div>
                  <a
                    href="/post/getting-a-crjmc-in-ontario-a-complete-guide-2025/"
                    className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-lg font-semibold rounded-lg text-white hover:bg-white hover:text-emerald-700 transition-colors duration-200 w-full sm:w-auto"
                  >
                    Learn More
                  </a>
                </div>
              </div>
              <div className="hidden md:flex justify-center">
                <picture>
                  <source
                    srcSet="/images/private-investigator/cleared-background-check-private-investigator-256.avif"
                    type="image/avif"
                  />
                  <source
                    srcSet="/images/private-investigator/cleared-background-check-private-investigator-256.webp"
                    type="image/webp"
                  />
                  <img
                    src="/images/private-investigator/cleared-background-check-private-investigator-256.png"
                    alt="Private Investigator with CRJMC"
                    className="max-w-[300px] h-auto rounded-lg shadow-xl"
                    loading="lazy"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof */}
        <section className="bg-gray-100 py-12">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <HeadingHighlight>
              More than 5526 happy customers (and counting)
            </HeadingHighlight>
            <TestimonialCarousel />
          </div>
        </section>
        {/* Process Steps */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <HeadingHighlight>
              Get Your CRJMC Online in 4 Easy Steps
            </HeadingHighlight>
            <p className="text-gray-600 mb-12 text-lg">
              Our streamlined online CRJMC check process is designed for speed
              and convenience. While traditional police stations take 3-6 weeks,
              <br />
              we deliver your official CRJMC results in just 15 minutes.
            </p>
            <div className="grid md:grid-cols-4 gap-6">
              <StepCard
                number="1"
                title="Quick Registration"
                duration="30 seconds"
              />
              <StepCard
                number="2"
                title="ID Verification"
                duration="3 - 5 minutes"
              />
              <StepCard
                number="3"
                title="Background Check"
                duration="8 - 10 minutes"
              />
              <StepCard
                number="4"
                title="Digital Delivery"
                duration="15 minutes total"
              />
            </div>
          </div>
        </section>

        {/* Who Needs Section */}
        <section className="bg-gray-100 py-16">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <HeadingHighlight>
              Who Needs To Complete The CRJMC Check Under The Youth Criminal
              Justice Act?
            </HeadingHighlight>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="flex justify-center">
                <picture>
                  <source
                    srcSet="/images/security-guard/security-guard-with-background-check-cleared-small.avif"
                    type="image/avif"
                  />
                  <source
                    srcSet="/images/security-guard/security-guard-with-background-check-cleared-small.webp"
                    type="image/webp"
                  />
                  <img
                    src="/images/security-guard/security-guard-with-background-check-cleared-small.png"
                    alt="Security Guard with Background Check"
                    className="max-w-[300px] h-auto rounded-lg"
                    loading="lazy"
                    aria-label="Security guard with background check results document"
                    title="Security guard with background check results document"
                  />
                </picture>
              </div>
              <div>
                <p className="mb-6">
                  Anyone who wants to work as a Licensed Security Guard or a
                  Licensed Private Investigator in Ontario must complete police
                  record check. If you are applying for a volunteer position,
                  you will need to provide a volunteer letter from the agency
                  where you intend to work. Background checks are required for
                  the following positions:
                </p>
                <div className="grid md:grid-cols-2 text-center gap-6 md:text-left">
                  <ul className="list-disc list-inside space-y-2">
                    <li>Private Investigators</li>
                    <li>Security Guards</li>
                    <li>Mall Security</li>
                    <li>Airport Security</li>
                  </ul>
                  <ul className="list-disc list-inside space-y-2">
                    <li>Special Event Security</li>
                    <li>Loss Prevention Security</li>
                    <li>Condominium Concierge</li>
                    <li>Close Protection</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <HeadingHighlight>Why Choose Our CRJMC Service?</HeadingHighlight>
            <div className="grid md:grid-cols-4 gap-8">
              <FeatureCard
                icon={Clock}
                title="100% Online Process"
                description="Complete your CRJMC check entirely online. No police station visits required - apply from anywhere in Ontario."
              />
              <FeatureCard
                icon={FileCheck}
                title="15-Minute Delivery"
                description="Get your official CRJMC results in just 15 minutes, compared to 3-6 weeks at police stations."
              />
              <FeatureCard
                icon={Shield}
                title="Authorized Provider"
                description="We're an authorized third-party provider. Our CRJMC checks are officially accepted across all Ontario regions."
              />
              <FeatureCard
                icon={CheckCircle}
                title="Renewal Savings"
                description={
                  <>
                    Save 20% on future CRJMC renewals.{" "}
                    <a
                      href="/renewal-discount"
                      className="text-emerald-700 hover:underline"
                    >
                      Learn about our discount program
                    </a>
                    .
                  </>
                }
              />
            </div>
          </div>
        </section>

        {/* Comparison Section - NEW */}
        <section className="py-16 bg-gradient-to-br from-emerald-50 via-white to-emerald-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <HeadingHighlight>
                Why Choose Online CRJMC Over A Police Station
              </HeadingHighlight>
              <p className="text-gray-600 mt-6 text-lg max-w-3xl mx-auto">
                Get your CRJMC faster, cheaper, and more conveniently than
                traditional methods
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-emerald-100 hover:border-emerald-300 transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-center mb-6">
                  <Clock className="w-12 h-12 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold mb-6 text-emerald-700 text-center">
                  Processing Time
                </h3>
                <div className="space-y-6">
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">CRJMC.net:</span>
                      <span className="text-emerald-700 font-bold text-lg">
                        15 minutes
                      </span>
                    </div>
                    <div className="w-full bg-emerald-200 h-2 rounded-full">
                      <div className="w-[5%] bg-emerald-600 h-2 rounded-full"></div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Police Station:</span>
                      <span className="text-gray-700">3-6 weeks</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full">
                      <div className="w-full bg-gray-400 h-2 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-emerald-100 hover:border-emerald-300 transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-center mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-12 h-12 text-emerald-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-6 text-emerald-700 text-center">
                  Cost Comparison
                </h3>
                <div className="space-y-6">
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">CRJMC.net:</span>
                      <span className="text-emerald-700 font-bold text-lg">
                        CAD${CRJMC_PRICE}
                      </span>
                    </div>
                    <div className="text-sm text-emerald-600">
                      ✓ No hidden fees
                    </div>
                    <div className="text-sm text-emerald-600">
                      ✓ 20% renewal discount
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Police Station:</span>
                      <span className="text-gray-700">CAD$60-70</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      × Additional travel costs
                    </div>
                    <div className="text-sm text-gray-500">
                      × Time off work needed
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-emerald-100 hover:border-emerald-300 transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-center mb-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-12 h-12 text-emerald-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-6 text-emerald-700 text-center">
                  Convenience & Security
                </h3>
                <div className="space-y-6">
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">CRJMC.net:</span>
                      <span className="text-emerald-700 font-bold">
                        100% Online
                      </span>
                    </div>
                    <div className="text-sm text-emerald-600">
                      ✓ 24/7 availability
                    </div>
                    <div className="text-sm text-emerald-600">
                      ✓ Secure digital delivery
                    </div>
                    <div className="text-sm text-emerald-600">
                      ✓ Instant verification
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Police Station:</span>
                      <span className="text-gray-700">In-Person</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      × Limited office hours
                    </div>
                    <div className="text-sm text-gray-500">
                      × Physical pickup required
                    </div>
                    <div className="text-sm text-gray-500">
                      × Long waiting times
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ontario Regions Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <HeadingHighlight>
                Online CRJMC - Valid Throughout Ontario
              </HeadingHighlight>
              <p className="text-gray-600 mt-6 text-lg max-w-3xl mx-auto">
                Our online CRJMC service is officially recognized and valid
                across all Ontario regions. Unlike traditional police services
                that require local residency, you can complete your CRJMC check
                with us regardless of your location. Save time and money - get
                your results in 15 minutes for just CAD${CRJMC_PRICE}, compared
                to 3-6 weeks and up to $70 at police stations.
              </p>
            </div>
            <div
              className={`relative ${!isRegionsExpanded ? "max-h-[500px] md:max-h-none overflow-hidden" : ""}`}
            >
              <RegionsList
                showDescriptions={true}
                columns={3}
                className="mt-8"
              />
              {!isRegionsExpanded && (
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 to-transparent md:hidden" />
              )}
            </div>
            <button
              onClick={() => setIsRegionsExpanded(!isRegionsExpanded)}
              className="mt-4 px-6 py-2 bg-white border border-emerald-200 rounded-lg text-emerald-700 hover:bg-emerald-50 transition-colors md:hidden"
            >
              {isRegionsExpanded ? "Show Less" : "View All Regions"}
            </button>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <HeadingHighlight>Frequently Asked Questions</HeadingHighlight>
              <p className="text-gray-600 mt-6 text-lg max-w-3xl mx-auto">
                Find answers to common questions about getting your Criminal
                Record & Judicial Matters Check online. For more detailed
                information, visit our{" "}
                <a href="/faq" className="text-emerald-700 hover:underline">
                  complete FAQ page
                </a>
                .
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-md border border-emerald-100">
                  <h3 className="text-lg font-bold mb-3 text-emerald-700">
                    How much does a CRJMC cost?
                  </h3>
                  <p className="text-gray-600">
                    The CRJMC costs <strong>CAD ${CRJMC_PRICE}</strong> at
                    CRJMC.net. This includes fast 15-minute processing, secure
                    online delivery, and no in-person visits. We also offer a{" "}
                    <a
                      href="/renewal-discount"
                      className="text-emerald-700 hover:underline"
                    >
                      20% discount on renewals
                    </a>
                    .
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border border-emerald-100">
                  <h3 className="text-lg font-bold mb-3 text-emerald-700">
                    What's included in a CRJMC?
                  </h3>
                  <p className="text-gray-600">
                    A CRJMC includes criminal convictions, outstanding charges
                    and arrest warrants, court orders (such as restraining
                    orders or probation orders), and absolute and conditional
                    discharges. Visit our{" "}
                    <a
                      href="/faq#what-is-included"
                      className="text-emerald-700 hover:underline"
                    >
                      FAQ page
                    </a>{" "}
                    for more details.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border border-emerald-100">
                  <h3 className="text-lg font-bold mb-3 text-emerald-700">
                    How long does it take to get my CRJMC?
                  </h3>
                  <p className="text-gray-600">
                    At CRJMC.net, you'll receive your police record check within
                    15 minutes. Our system is fully automated and designed to
                    provide fast, secure results, delivered directly to your
                    inbox. There's no need for an in-person appointment.
                  </p>
                </div>
              </div>
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-md border border-emerald-100">
                  <h3 className="text-lg font-bold mb-3 text-emerald-700">
                    Do I need to visit a police station?
                  </h3>
                  <p className="text-gray-600">
                    No! Everything is done online from start to finish. Unlike
                    traditional{" "}
                    <a
                      href="/ontario"
                      className="text-emerald-700 hover:underline"
                    >
                      police services in Ontario
                    </a>
                    , there's no need to visit a station or book an appointment.
                    Simply complete your request online and get results in 15
                    minutes.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border border-emerald-100">
                  <h3 className="text-lg font-bold mb-3 text-emerald-700">
                    Who needs a CRJMC?
                  </h3>
                  <p className="text-gray-600">
                    A CRJMC is commonly required for security guards, private
                    investigators, mall security, airport security, tow
                    operators and other roles. Check our{" "}
                    <a
                      href="/posts/getting-a-crjmc-in-ontario-a-complete-guide-2025"
                      className="text-emerald-700 hover:underline"
                    >
                      complete guide
                    </a>{" "}
                    for detailed information.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md border border-emerald-100">
                  <h3 className="text-lg font-bold mb-3 text-emerald-700">
                    Is the process secure?
                  </h3>
                  <p className="text-gray-600">
                    Yes, our process is secure and fully compliant with Canadian
                    privacy laws. We use advanced ID verification technology to
                    verify your identity online, ensuring your information
                    remains safe and confidential. Read our{" "}
                    <a
                      href="/privacy-policy"
                      className="text-emerald-700 hover:underline"
                    >
                      privacy policy
                    </a>{" "}
                    for more details.
                  </p>
                </div>
              </div>
            </div>
            <div className="text-center mt-8">
              <a
                href="/faq"
                className="inline-flex items-center justify-center px-6 py-3 border border-emerald-700 text-lg font-semibold rounded-lg text-emerald-700 hover:bg-emerald-50 transition-colors"
              >
                View All FAQs
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-emerald-700 text-white py-16">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="hidden md:block">
                <div className="relative w-full">
                  <picture>
                    <source
                      srcSet="/images/cleared-background-check-512.avif"
                      type="image/avif"
                    />
                    <source
                      srcSet="/images/cleared-background-check-512.webp"
                      type="image/webp"
                    />
                    <img
                      src="/images/cleared-background-check-512.png"
                      alt="Cleared CRJMC"
                      className="w-full max-h-[350px] object-cover object-[10%_79%]"
                      loading="lazy"
                      aria-label="Visual representation of the CRJMC result sheet"
                      title="Visual representation of the CRJMC result sheet"
                    />
                  </picture>
                </div>
              </div>
              <div className="text-center">
                <h2 className="text-3xl font-bold mb-6">
                  Get your CRJMC today!
                </h2>
                <div className="space-y-4 mb-8">
                  <p className="text-xl">✓ Takes less than 15 minutes</p>
                  <p className="text-xl">✓ 100% online application</p>
                  <p className="text-xl">
                    ✓ Issued by an Authorized Third Party
                  </p>
                </div>
                <div className="max-w-[430px] mx-auto">
                  <ButtonCTA />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CRJMCLanding;
